import jwtDecode from 'jwt-decode';

// JwtSecurityTokenHandler shortens some claim types on the way out -- NameIdentifier becomes
// nameid, Name becomes unique_name -- but ClaimTypes.Sid is not in that map, so the org id is
// written under its full XML-schema URI instead. Reading `sid` alone always came back undefined,
// which silently made every org-scoped check in the client fail closed. The short name is tried
// first so this keeps working if the token is ever normalised server-side.
const OrgIdClaimKeys = [
    'sid',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid',
];

export default class AuthUtil {
    static getUsername(userToken) {
        if(!userToken) {
            return '';
        }
        return jwtDecode(userToken).unique_name ?? '';
    }

    static isAdmin(userToken) {
        return userToken && (jwtDecode(userToken).Admin === 'True');
    }

    static isAuthorized(userToken) {
        return !!userToken;
    }

    static isTokenExpired(userToken) {
        if(!userToken) {
            return true;
        }
        const { exp } = jwtDecode(userToken);

        return Date.now() > (exp * 1000);
    }

    static getUserId(userToken) {
        if(!userToken) {
            return '';
        }
        return jwtDecode(userToken).nameid ?? '';
    }

    static canChangeHazards(userToken) {
        if(!userToken) {
            return false;
        }

        return jwtDecode(userToken).CanChangeHazards === 'True';
    }

    static getProjectUserXRefs(userToken) {
        const tokenDecode = jwtDecode(userToken);
        const permissions = tokenDecode.ProjectPermissions;
        let parsedPermissions = [];

        if(Array.isArray(permissions)) {
            permissions.forEach((perm) => parsedPermissions.push(JSON.parse(perm.replaceAll('\'', '"'))));
        } else {
            parsedPermissions.push(JSON.parse(permissions.replaceAll('\'', '"')));
        }

        return parsedPermissions;
    }

    static isSuperAdmin(userToken) {
        const tokenDecode = jwtDecode(userToken);
        return tokenDecode.SuperAdmin === 'True';
    }

    /**
     * True when this session is a super admin acting as another user. The claim is only present on
     * impersonated tokens, so a missing claim reads as false.
     */
    static isImpersonating(userToken) {
        if(!userToken) {
            return false;
        }
        return jwtDecode(userToken).IsImpersonating === 'True';
    }

    /**
     * Username of the super admin behind an impersonated session, or '' for a normal session.
     */
    static getImpersonatorUsername(userToken) {
        if(!userToken) {
            return '';
        }
        return jwtDecode(userToken).ImpersonatorUsername ?? '';
    }

    static isTranslator(userToken) {
        if(!userToken) {
            return false;
        }
        return jwtDecode(userToken).Translator === 'True';
    }

    // Static like every other helper here: it was declared on the prototype while AuthUtil is
    // only ever used as a bare class, so AuthUtil.getUsersOrgId() was undefined. Nothing called
    // it, which is why both that and the claim key below went unnoticed.
    //
    // Returns a number: the claim arrives as a string, and callers compare it against org ids
    // from the API, which are numeric.
    static getUsersOrgId(userToken) {
        if (!userToken) {
            return null;
        }

        const claims = jwtDecode(userToken);
        const raw = OrgIdClaimKeys.map((key) => claims[key]).find((value) => value != null);

        if (raw == null) {
            return null;
        }

        const orgId = Number(raw);

        return Number.isNaN(orgId) ? null : orgId;
    }
}
